import { Button, Tag } from "antd";
import { StarOutlined } from "@ant-design/icons";
import { IMAGE_NOT_FOUND } from "constants/common";
import NInputNumber from "components/Desktop/NInputNumber";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import ProductImage from "components/ProductImage";

interface DataType {
  id: number;
  key: number;
  productName: string;
  price: string;
  qty: number;
  imageUrl: string;
  productId: number;
  productCode?: string;
  isRedemptionItem?: boolean;
  redemptionItem?: any; // RedemptionItem type
}

interface MobileCartItemProps {
  item: DataType;
  index: number;
  showCheckbox?: boolean;
  isSelected?: boolean;
  onSelect?: (item: DataType, checked: boolean) => void;
  onChangeQuantity?: (qty: number, itemId: number) => void;
  onDelete?: (itemId: number) => void;
  readOnly?: boolean;
}

const MobileCartItem = ({
  item,
  index,
  showCheckbox = false,
  isSelected = false,
  onSelect,
  onChangeQuantity,
  onDelete,
  readOnly = false,
}: MobileCartItemProps) => {
  const navigate = useNavigate();

  const handleItemClick = (e: React.MouseEvent) => {
    // Prevent navigation if clicking on checkbox or input
    if (
      e.target instanceof HTMLInputElement ||
      (e.target as HTMLElement).closest(".ant-input-number") ||
      (e.target as HTMLElement).closest(".checkbox-area")
    ) {
      return;
    }

    // Navigate based on item type
    if (item.isRedemptionItem) {
      navigate("/capsule-market");
    } else if (item.productId) {
      navigate(`/product/${item.productId}`);
    }
  };

  return (
    <div className="mb-4 cursor-pointer" onClick={handleItemClick}>
      <div className="flex flex-col">
        <div className="flex gap-4">
          {showCheckbox && (
            <div className="flex items-center checkbox-area">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => onSelect?.(item, e.target.checked)}
                className="w-4 h-4"
              />
            </div>
          )}
          <ProductImage
            imageUrl={item.imageUrl}
            productCode={item.productCode}
            width={90}
            height={90}
            alt={item.productName}
            className="object-contain"
            fallback={IMAGE_NOT_FOUND}
          />
          <div className="flex-1 flex flex-col justify-between">
            <div className="flex justify-between">
              <div className="line-clamp-2 mb-2">
                {item.productName}
                {item.isRedemptionItem && (
                  <div className="mt-1">
                    <Tag color="purple" icon={<StarOutlined />} size="small">
                      Redemption Item
                    </Tag>
                    {item.redemptionItem && (
                      <div className="text-xs text-gray-600 mt-1">
                        {item.redemptionItem.pointsCost} points
                        {item.redemptionItem.type === "HYBRID" &&
                          ` + ${item.redemptionItem.cashCost} THB`}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {!readOnly && onDelete && (
                <Button
                  type="text"
                  danger
                  className="delete-button"
                  icon={<Trash2 />}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent navigation when clicking delete
                    onDelete(item.id);
                  }}
                />
              )}
            </div>
            <div className="flex items-center justify-between">
              {readOnly ? (
                <div>จำนวน: {item.qty}</div>
              ) : (
                <NInputNumber
                  value={item.qty}
                  onChange={(e) => onChangeQuantity?.(Number(e), item.id)}
                  disabled={false} // Enable quantity change for all items including redemption items
                />
              )}
              <div className="font-semibold">
                {item.isRedemptionItem ? (
                  <div>
                    <div className="text-primary-500">
                      {item.redemptionItem?.pointsCost} points
                    </div>
                    {item.redemptionItem?.type === "HYBRID" && (
                      <div className="text-sm">
                        + {(item.qty * parseFloat(item.price)).toLocaleString()}{" "}
                        บาท
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    ฿{(item.qty * parseFloat(item.price)).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileCartItem;
